// src/components/Pimgy/IndividualTransactionHistory.js
import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { formatDateToDDMMYYYY } from '../../utils/dateUtils';
import '../AdminDashboard/ExpenseManager.css';

const IndividualTransactionHistory = () => {
  const [searchValue, setSearchValue] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    if (!searchValue.trim()) {
      alert('Please enter a registration number or name to search');
      return;
    }

    try {
      setLoading(true);
      setSearched(true);
      const response = await axios.get(`${API_BASE_URL}/api/pimgy/individual-history`, {
        params: { search_value: searchValue.trim() }
      });
      setTransactions(response.data);
    } catch (error) {
      console.error('Error fetching individual history:', error);
      alert(error.response?.data?.error || 'Failed to load transaction history');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchValue('');
    setTransactions([]);
    setSearched(false);
  };

  const printTransactionHistory = () => {
    if (transactions.length === 0) {
      alert('No transactions to print.');
      return;
    }

    const total = transactions.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
    const searchInfo = transactions.length > 0 
      ? `Registration No/Name: ${transactions[0].registration_no} - ${transactions[0].name}`
      : '';

    const historyContent = `
<style>
  body { font-family: Arial, sans-serif; margin: 20px; padding: 20px; }
  .header { text-align: center; font-weight: bold; font-size: 1.5em; margin-bottom: 5px; }
  .sub-header { text-align: center; font-size: 1em; margin-bottom: 15px; }
  .search-info { text-align: center; margin-bottom: 20px; font-weight: bold; }
  table { width: 100%; border-collapse: collapse; margin-top: 20px; }
  th, td { border: 1px solid #000; padding: 8px; text-align: left; }
  th { background-color: #f0f0f0; font-weight: bold; }
  .right { text-align: right; }
  .total-row { background-color: #f0f0f0; font-weight: bold; }
  .footer { text-align: center; margin-top: 30px; font-size: 0.9em; }
</style>

<div class="header">M/S SHREE MAHAKAL FINANCE AND LEASING (R)</div>
<div class="sub-header">Khajuri, Aland , Kalaburgi</div>
<div class="search-info">Pigmy Collection - Individual Transaction History</div>
<div class="search-info">${searchInfo}</div>

<table>
  <thead>
    <tr>
      <th>Date</th>
      <th>Registration No</th>
      <th>Name</th>
      <th>Transaction Type</th>
      <th>Finance Name</th>
      <th class="right">Amount (₹)</th>
      <th>Payment Mode</th>
      <th>Transaction ID</th>
    </tr>
  </thead>
  <tbody>
    ${transactions.map(t => `
      <tr>
        <td>${formatDateToDDMMYYYY(t.transaction_date)}</td>
        <td>${t.registration_no}</td>
        <td>${t.name}</td>
        <td>${t.transaction_type}</td>
        <td>${t.finance_name || '-'}</td>
        <td class="right">${Number(t.amount).toFixed(2)}</td>
        <td>${t.payment_mode || '-'}</td>
        <td>${t.transaction_id || '-'}</td>
      </tr>
    `).join('')}
    <tr class="total-row">
      <td colspan="5" style="text-align: right;"><strong>TOTAL:</strong></td>
      <td class="right"><strong>₹ ${total.toFixed(2)}</strong></td>
      <td colspan="2"></td>
    </tr>
  </tbody>
</table>

<div class="footer">
  Printed on: ${formatDateToDDMMYYYY(new Date().toISOString().split('T')[0])}
</div>
`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(historyContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="transaction-history-section">
      <h3>Individual Transaction History</h3>
      
      <div className="filters-container">
        <div className="filter-group" style={{ flex: 1 }}>
          <label>Search by Registration No or Name:</label>
          <input
            type="text"
            className="filter-input"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            placeholder="Enter registration number or name"
          />
        </div>
        <div className="filter-buttons">
          <button 
            className="clear-button" 
            onClick={clearSearch}
            style={{ marginRight: '10px' }}
          >
            Clear
          </button>
          <button 
            className="print-button" 
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
          {transactions.length > 0 && (
            <button 
              className="print-button" 
              onClick={printTransactionHistory}
              style={{ marginLeft: '10px' }}
            >
              Print
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>Loading transactions...</div>
      ) : searched && transactions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          No transactions found for the search criteria.
        </div>
      ) : transactions.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table className="transactions-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Registration No</th>
                <th>Name</th>
                <th>Transaction Type</th>
                <th>Finance Name</th>
                <th>Amount (₹)</th>
                <th>Payment Mode</th>
                <th>Transaction ID</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t) => (
                <tr key={t.id}>
                  <td>{formatDateToDDMMYYYY(t.transaction_date)}</td>
                  <td>{t.registration_no}</td>
                  <td>{t.name}</td>
                  <td>{t.transaction_type}</td>
                  <td>{t.finance_name || '-'}</td>
                  <td>₹{Number(t.amount).toFixed(2)}</td>
                  <td>{t.payment_mode || '-'}</td>
                  <td>{t.transaction_id || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          Enter a registration number or name to search for transaction history.
        </div>
      )}
    </div>
  );
};

export default IndividualTransactionHistory;

